import { apiClient } from './client';

export interface RewardAddresses {
  coreum?: string;
  xrp?: string;
  [key: string]: string | undefined;
}

export interface UserSettings {
  id: string;
  username: string;
  bio: string | null;
  avatar_url: string | null;
  xrpl_address: string;
  email: string | null;
  email_confirmed: boolean;
  social_links: {
    x_account: string | null;
    website: string | null;
    discord_id: string | null;
    discord_username: string | null;
    discord_avatar: string | null;
    discord_in_server: boolean | null;
  } | null;
  reward_addresses: RewardAddresses;
  created_at: string;
}

export interface UpdateSettingsData {
  username?: string;
  bio?: string | null;
  social_links?: {
    x_account?: string | null;
    website?: string | null;
  };
}

export interface WalletOfferControls {
  wallet_offers_disabled: boolean;
  wallet_offer_control_mode: 'none' | 'floor_relative';
  wallet_offer_floor_percentage: number;
  wallet_trade_offers_disabled: boolean;
}

export interface NotificationPreferences {
  notify_on_offer_received: boolean;
  notify_on_nft_sold: boolean;
  notify_on_nft_bought: boolean;
  notify_on_offer_accepted: boolean;
  notify_on_transfer_offer: boolean;
  notify_on_mint_reminders: boolean;
}

/**
 * Get current user settings
 */
export async function getSettings(): Promise<UserSettings> {
  try {
    const response = await apiClient.get<UserSettings>('/settings');
    return response;
  } catch (error) {
    console.error('Error fetching settings:', error);
    throw error;
  }
}

/**
 * Update user settings
 */
export async function updateSettings(data: UpdateSettingsData): Promise<UserSettings> {
  try {
    // Get CSRF token first
    const csrfToken = await getCsrfToken();

    const response = await apiClient.request<UserSettings>('/settings', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(data),
    });

    return response;
  } catch (error) {
    console.error('Error updating settings:', error);
    throw error;
  }
}

/**
 * Upload avatar image
 */
export async function uploadAvatar(file: File): Promise<{ avatar_url: string }> {
  try {
    // Get CSRF token first
    const csrfToken = await getCsrfToken();

    const formData = new FormData();
    formData.append('avatar', file);

    const url = `${apiClient['baseUrl']}/settings/avatar`;
    const token = localStorage.getItem('xrpl_marketplace_token');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'X-CSRF-Token': csrfToken,
      },
      body: formData,
      credentials: 'include', // Required for CSRF cookies
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to upload avatar');
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading avatar:', error);
    throw error;
  }
}

/**
 * Get CSRF token
 */
export async function getCsrfToken(): Promise<string> {
  try {
    const response = await apiClient.get<{ csrf_token: string }>('/csrf-token');
    return response.csrf_token;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    throw error;
  }
}

/**
 * Set email address
 */
export async function setEmail(email: string): Promise<{ message: string; email: string; email_confirmed: boolean }> {
  try {
    const csrfToken = await getCsrfToken();

    const response = await apiClient.request<{ message: string; email: string; email_confirmed: boolean }>('/email/set', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ email }),
    });

    return response;
  } catch (error) {
    console.error('Error setting email:', error);
    throw error;
  }
}

/**
 * Remove email address
 */
export async function removeEmail(): Promise<{ message: string }> {
  try {
    const csrfToken = await getCsrfToken();

    const response = await apiClient.request<{ message: string }>('/email/remove', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({}),
    });

    return response;
  } catch (error) {
    console.error('Error removing email:', error);
    throw error;
  }
}

/**
 * Resend email confirmation
 */
export async function resendConfirmation(): Promise<{ message: string }> {
  try {
    const csrfToken = await getCsrfToken();

    const response = await apiClient.request<{ message: string }>('/email/resend-confirmation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({}),
    });

    return response;
  } catch (error) {
    console.error('Error resending confirmation:', error);
    throw error;
  }
}

/**
 * Check if user is first login
 */
export async function isFirstLogin(): Promise<{ is_first_login: boolean; user_id: string }> {
  try {
    const response = await apiClient.get<{ is_first_login: boolean; user_id: string }>('/auth/is-first-login');
    return response;
  } catch (error) {
    console.error('Error checking first login:', error);
    throw error;
  }
}

/**
 * Get notification preferences
 */
export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  try {
    const response = await apiClient.get<NotificationPreferences>('/settings/notifications');
    return response;
  } catch (error) {
    console.error('Error fetching notification preferences:', error);
    throw error;
  }
}

/**
 * Update notification preferences
 */
export async function updateNotificationPreferences(
  preferences: Partial<NotificationPreferences>
): Promise<NotificationPreferences> {
  try {
    const csrfToken = await getCsrfToken();

    const response = await apiClient.request<NotificationPreferences>('/settings/notifications', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(preferences),
    });

    return response;
  } catch (error) {
    console.error('Error updating notification preferences:', error);
    throw error;
  }
}

/**
 * Get wallet offer controls (authenticated user)
 */
export async function getWalletOfferControls(): Promise<WalletOfferControls> {
  try {
    const response = await apiClient.get<WalletOfferControls>('/settings/offer-controls');
    return response;
  } catch (error) {
    console.error('Error fetching wallet offer controls:', error);
    throw error;
  }
}

/**
 * Update wallet offer controls
 */
export async function updateWalletOfferControls(
  controls: Partial<WalletOfferControls>
): Promise<WalletOfferControls> {
  try {
    const csrfToken = await getCsrfToken();

    const response = await apiClient.request<WalletOfferControls>('/settings/offer-controls', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(controls),
    });

    return response;
  } catch (error) {
    console.error('Error updating wallet offer controls:', error);
    throw error;
  }
}

/**
 * Get public wallet offer controls for any address (no auth required)
 */
export async function getPublicWalletOfferControls(address: string): Promise<WalletOfferControls> {
  try {
    const response = await apiClient.get<WalletOfferControls>(`/wallets/${address}/offer-controls`);
    return response;
  } catch (error) {
    console.error('Error fetching public wallet offer controls:', error);
    throw error;
  }
}

/**
 * Get reward addresses
 */
export async function getRewardAddresses(): Promise<{ reward_addresses: RewardAddresses }> {
  try {
    const response = await apiClient.get<{ reward_addresses: RewardAddresses }>('/settings/reward-addresses');
    return response;
  } catch (error) {
    console.error('Error fetching reward addresses:', error);
    throw error;
  }
}

/**
 * Update reward addresses
 */
export async function updateRewardAddresses(
  addresses: RewardAddresses
): Promise<{ reward_addresses: RewardAddresses }> {
  try {
    const csrfToken = await getCsrfToken();

    const response = await apiClient.request<{ reward_addresses: RewardAddresses }>('/settings/reward-addresses', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ reward_addresses: addresses }),
    });

    return response;
  } catch (error) {
    console.error('Error updating reward addresses:', error);
    throw error;
  }
}

/**
 * Get Discord link URL (navigates user to Discord OAuth)
 * @param returnTo - Optional path to redirect to after linking (defaults to /settings)
 */
export function getDiscordLinkUrl(returnTo?: string): string {
  const baseUrl = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';
  const token = localStorage.getItem('xrpl_marketplace_token');
  let url = `${baseUrl}/auth/discord/link?token=${encodeURIComponent(token || '')}`;
  if (returnTo) {
    url += `&returnTo=${encodeURIComponent(returnTo)}`;
  }
  return url;
}

/**
 * Unlink Discord account
 */
export async function unlinkDiscord(): Promise<{ success: boolean }> {
  const csrfToken = await getCsrfToken();
  return apiClient.request<{ success: boolean }>('/auth/discord/unlink', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({}),
  });
}
